import '@cortex/ui-kit/tokens.css';
import '@cortex/ui-kit/components.css';
import '@cortex/ui-kit/primitives.css';
import '@cortex/ui-kit/shell-components.css';
import '@cortex/ui-kit/triage-components.css';
// Canonical chat package styles (S4, DEV-16991) — the `/chat` surface renders
// its composable components (PaChatView, PaThreadList, AgentChat, Composer).
import '@formant/chat/styles.css';

import { CortexProvider } from '@cortex/react-query';
import { fork } from '@cortex/utils/fork';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode, useCallback, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { AuthProvider, useAuth } from './auth/AuthProvider';
import { playbookOrigin, playbookRedirect } from './auth/playbook-redirect';
import { AuthLoadingScreen } from './components/common/AuthLoadingScreen';
import { ToastProvider } from './components/common/Toast';
import { PublicShareView } from './public-share/PublicShareView';
import { routeTree } from './routeTree.gen';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  // TanStack Router requires this exact augmentation interface name.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Register {
    router: typeof router;
  }
}

// Hosted dev backend is the default — set VITE_API_URL=http://localhost:3000/api
// in a .env.local for local-stack development.
const apiUrl = import.meta.env.VITE_API_URL ?? 'https://api.cortex-dev.formant.ai/api';
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find root element');
}

// Invite-flow recovery: when a user clicks an invite action-token link
// while another user is still signed in, the metaphysics-themed Keycloak
// error page hands them through KC's logout endpoint, with the original
// action-token URL stashed in this query param. Bounce them straight to
// that URL before AuthProvider kicks off its own OIDC dance — otherwise
// we'd sign the wrong user back in instead of letting the action token
// redeem cleanly.
// Sign-out completion: when `signOut` redirected the browser to KC's
// logout endpoint, KC bounces back here with `?signed_out=1` appended
// to the post_logout_redirect_uri. At that point we need to nuke any
// residual `oidc.*` entries from localStorage BEFORE AuthProvider boots
// — otherwise the stored user blob makes `mgr.getUser()` return the
// pre-logout user and the app treats the session as still valid. We
// then strip the query param and hand control to the rest of the IIFEs
// (which will end up at signinRedirect, this time without an active KC
// session, so the login form is shown).
(function maybeCompleteSignOut() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signed_out') !== '1') return;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith('oidc.')) localStorage.removeItem(k);
    }
    params.delete('signed_out');
    const qs = params.toString();
    const clean = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', clean);
  } catch (err) {
    console.warn('console/signed-out: cleanup failed', err);
  }
})();

(function maybeContinueInvite() {
  try {
    const params = new URLSearchParams(window.location.search);
    const next = params.get('invite_continue');
    if (!next) return;
    let target: URL;
    try {
      target = new URL(next, window.location.origin);
    } catch {
      return;
    }
    // Only accept Keycloak's login-actions URLs from the configured Keycloak
    // origin. The origin check is what prevents this from being an open
    // redirect — without it, `?invite_continue=https://evil.example/realms/x/
    // login-actions/y` would match the pathname regex and we'd redirect to
    // an attacker-controlled host.
    const authority =
      import.meta.env.VITE_KEYCLOAK_AUTHORITY ??
      'https://keycloak.cortex-dev.formant.ai/realms/formant';
    let kcOrigin: string;
    try {
      kcOrigin = new URL(authority).origin;
    } catch {
      return;
    }
    if (target.origin !== kcOrigin) return;
    if (!/\/realms\/[^/]+\/login-actions\//.test(target.pathname)) return;
    window.location.replace(target.toString());
  } catch (err) {
    console.warn('console/invite-continue: bounce failed', err);
  }
})();

function AppShell() {
  const { token, ready, isAuthenticated, signIn } = useAuth();
  const redirectStarted = useRef(false);

  // Auto-redirect to the resolved login provider (Coherence by default; Keycloak
  // for a ?kc_idp_hint deep link or ?login=keycloak) when the bootstrap finished
  // and there's no session. `signIn()` is strategy-agnostic. The ref guard
  // prevents a redirect storm if signinRedirect throws synchronously.
  useEffect(() => {
    if (ready && !isAuthenticated && !redirectStarted.current) {
      redirectStarted.current = true;
      fork(
        (async () => {
          try {
            await signIn();
          } catch (err) {
            redirectStarted.current = false;
            console.error('Failed to start OIDC sign-in', err);
          }
        })(),
      );
    }
  }, [ready, isAuthenticated, signIn]);

  // A 401 from the cortex API means the session expired or was invalidated
  // server-side despite automaticSilentRenew. Bounce through Keycloak (silent
  // if the SSO cookie is alive, interactive otherwise) rather than letting a
  // raw 401 surface as a red error in the UI. The ref guards against a storm
  // of concurrent 401s each firing signinRedirect before navigation lands.
  const reauthStarted = useRef(false);
  const onUnauthorized = useCallback(() => {
    if (reauthStarted.current) return;
    reauthStarted.current = true;
    fork(
      (async () => {
        try {
          await signIn();
        } catch (err) {
          reauthStarted.current = false;
          console.error('Failed to re-authenticate after 401', err);
        }
      })(),
    );
  }, [signIn]);

  // Bootstrapping, or resolved-but-unauthenticated (about to redirect to the
  // login provider). Show the branded splash that matches index.html's static
  // `#boot-splash`, so the sign-in bounce reads as one continuous loading state
  // rather than a flash between navigations.
  if (!ready || !isAuthenticated) {
    return <AuthLoadingScreen />;
  }
  return (
    <CortexProvider baseUrl={apiUrl} jwt={token ?? undefined} onUnauthorized={onUnauthorized}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </CortexProvider>
  );
}

// Public dashboard shares are reached at /share/:shareId by anyone with the
// link, with no Keycloak session. AppShell's auth gate wraps the ENTIRE
// router (RouterProvider never mounts until authenticated), so a plain
// file-based route here would never be reachable while logged out — this
// branch renders a standalone tree instead, entirely outside AuthProvider/
// the router/nav chrome, using a CortexProvider client built with no jwt
// (its beforeRequest hook only attaches Authorization when a jwt is given,
// so this client naturally sends no bearer — matching the API's @Public()
// share routes).
const shareId = /^\/share\/([^/]+)\/?$/.exec(window.location.pathname)?.[1];

// Robot onboarding moved to the playbook app: any `/robots/**` load (incl.
// signup verify's `/robots?setup=1&token=…`) leaves before AuthProvider
// strips the refresh token and bounces to Coherence login.
const playbookDest = playbookRedirect(
  window.location.href,
  playbookOrigin(window.location.hostname, import.meta.env.VITE_ROBOT_ONBOARDING_URL),
);
if (playbookDest) {
  window.location.replace(playbookDest);
}

if (!playbookDest) {
  createRoot(rootElement).render(
    <StrictMode>
      {shareId ? (
        <CortexProvider baseUrl={apiUrl} onUnauthorized={() => undefined}>
          <PublicShareView shareId={shareId} />
        </CortexProvider>
      ) : (
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      )}
    </StrictMode>,
  );
}
